import React, { useState, useEffect } from 'react';
import api from '../config/axios';
import VignetteTournoi from './imgVignetteTournoi';
import iconeTennis from '../assets/images/icone-tennis.png';
import iconeCalendar from '../assets/images/icone-calendar.png';
import imgTournoi9 from '../assets/images/img-tournoi-9.png';
import { generateCalendarLink } from '../utils/calendar';

interface TournoiProps {
  tournoi: {
    tournoiId: number;
    tournoiLib: string;
    tournoiDateDebut: string;
    tournoiDateFin: string;
    clubId: number;
    ligue: string;
    categorie: string;
    epreuve: string;
    dateAjout: string;
  };
}

const TournoiCard: React.FC<TournoiProps> = ({ tournoi }) => {
  const [showClubDetails, setShowClubDetails] = useState(false);
  const [club, setClub] = useState<any>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {

    // Vérifier si l'utilisateur est connecté
    const token = sessionStorage.getItem('accessToken');
    setIsLoggedIn(!!token);

    const fetchClubDetails = async () => {
      try {
        const response = await api.get(`/club/${tournoi.clubId}`);        
        setClub(response.data);

      } catch (error) {
        console.error('Error fetching club details:', error);
      }
    };

    fetchClubDetails();
  }, [tournoi.clubId]);

// Fonctions pour générer des liens de calendrier pour différentes plateformes
const generateGoogleCalendarLink = () => {
  const startDate = new Date(tournoi.tournoiDateDebut);
  const endDate = new Date(tournoi.tournoiDateFin);
  
  // Format pour Google Calendar
  const startDateString = startDate.toISOString().replace(/-|:|\.\d+/g, '');
  const endDateString = endDate.toISOString().replace(/-|:|\.\d+/g, '');
  
  const location = club ? encodeURIComponent(`${club.clubLib}, ${club.clubAdresse}`) : '';
  const title = encodeURIComponent(tournoi.tournoiLib);
  const details = encodeURIComponent(`Catégorie: ${tournoi.categorie} - Épreuve: ${tournoi.epreuve}`);
  
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDateString}/${endDateString}&details=${details}&location=${location}`;
};

const generateOutlookCalendarLink = () => {
  const startDate = new Date(tournoi.tournoiDateDebut);
  const endDate = new Date(tournoi.tournoiDateFin);
  
  // Format pour Outlook Online
  const startDateString = startDate.toISOString();
  const endDateString = endDate.toISOString();
  
  const location = club ? encodeURIComponent(`${club.clubLib}, ${club.clubAdresse}`) : '';
  const title = encodeURIComponent(tournoi.tournoiLib);
  const details = encodeURIComponent(`Catégorie: ${tournoi.categorie} - Épreuve: ${tournoi.epreuve}`);
  
  return `https://outlook.office.com/calendar/action/compose?subject=${title}&startdt=${startDateString}&enddt=${endDateString}&body=${details}&location=${location}`;
};

const generateYahooCalendarLink = () => {
  const startDate = new Date(tournoi.tournoiDateDebut);
  const endDate = new Date(tournoi.tournoiDateFin);
  
  // Format pour Yahoo Calendar
  const startDateString = startDate.toISOString().replace(/-|:|\.\d+/g, '');
  const endDateString = endDate.toISOString().replace(/-|:|\.\d+/g, '');
  
  const location = club ? encodeURIComponent(`${club.clubLib}, ${club.clubAdresse}`) : '';
  const title = encodeURIComponent(tournoi.tournoiLib);
  const details = encodeURIComponent(`Catégorie: ${tournoi.categorie} - Épreuve: ${tournoi.epreuve}`);
  
  return `https://calendar.yahoo.com/?title=${title}&st=${startDateString}&et=${endDateString}&desc=${details}&in_loc=${location}`;
};

// Pour iOS/macOS, on utilise toujours un fichier .ics mais avec un lien direct
const generateAppleCalendarLink = () => {
  const calendarLink = generateCalendarLink(
    tournoi.tournoiLib,
    tournoi.tournoiDateDebut,
    tournoi.tournoiDateFin,
    club ? `${club.clubLib}, ${club.clubAdresse}` : '',
    `Catégorie: ${tournoi.categorie} - Épreuve: ${tournoi.epreuve}`
  );
  
  // Pour Apple Calendar, on utilise webcal:// au lieu de data:
  // Mais comme cela nécessite un serveur pour héberger le fichier .ics, 
  // nous gardons la méthode de téléchargement pour l'instant
  return calendarLink;
};
  
const handleAppleCalendarDownload = (e: React.MouseEvent) => {
  e.preventDefault();
  
  // Assurez-vous que les dates sont au format correct (chaîne ISO)
  const startDate = new Date(tournoi.tournoiDateDebut).toISOString();
  const endDate = new Date(tournoi.tournoiDateFin).toISOString();
  
  const calendarLink = generateCalendarLink(
    tournoi.tournoiLib,
    startDate,
    endDate,
    club ? `${club.clubLib}, ${club.clubAdresse}` : '',
    `Catégorie: ${tournoi.categorie} - Épreuve: ${tournoi.epreuve}`
  );
  
  // Créer et cliquer sur un lien invisible pour télécharger le fichier .ics
  const link = document.createElement('a');
  link.href = calendarLink;
  link.download = `tournoi-padel-${tournoi.tournoiId}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

  return (
    <div className="card w-full bg-base-100 shadow-xl mb-4 max-w-2xl mx-auto">
      {isLoggedIn && (
        <div className="absolute top-2 right-2 dropdown dropdown-end">
          <label tabIndex={0} className="btn btn-ghost btn-circle p-2 bg-white rounded-full shadow-md hover:bg-orange-100 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m-3-3h6" />
            </svg>
          </label>
          <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52 mt-2">
            <li>
              <a href={generateGoogleCalendarLink()} target="_blank" rel="noopener noreferrer" className="flex items-center">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#4285F4">
                  <path d="M21.56 2H17V0h-2v2H9V0H7v2H2.44A2.44 2.44 0 0 0 0 4.44v17.12A2.44 2.44 0 0 0 2.44 24h19.12A2.44 2.44 0 0 0 24 21.56V4.44A2.44 2.44 0 0 0 21.56 2zm.44 19.56c0 .24-.2.44-.44.44H2.44c-.24 0-.44-.2-.44-.44V7h20v14.56z"/>
                </svg>
                Google Calendar
              </a>
            </li>
            <li>
              <a href={generateOutlookCalendarLink()} target="_blank" rel="noopener noreferrer" className="flex items-center">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#0078D4">
                  <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.54.22.34.31.77.1.43.1.89zm4.69 5.01q.46.46 1.11.46.63 0 1.09-.46.45-.45.45-1.08 0-.64-.45-1.09-.45-.45-1.09-.45-.66 0-1.12.46-.46.45-.46 1.08 0 .64.47 1.08z"/>
                </svg>
                Outlook
              </a>
            </li>
            <li>
              <a href={generateYahooCalendarLink()} target="_blank" rel="noopener noreferrer" className="flex items-center">
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#6001D2">
                  <path d="M19.828 3L12 13.438 4.172 3H2l9.172 12.188L11.766 21h2.468l.594-5.812L24 3z"/>
                </svg>
                Yahoo Calendar
              </a>
            </li>
            <li>
            <a 
              href="#" 
              onClick={handleAppleCalendarDownload}
              className="flex items-center"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="#000000">
                <path d="M17.68 7.28c-.93 0-1.73.4-2.36 1.05-.5.5-1 1.37-1.14 2.13-.06.35-.06.72 0 1.06.15.78.57 1.58 1.13 2.13.64.64 1.43 1.07 2.37 1.07.93 0 1.72-.4 2.35-1.07.57-.57.99-1.35 1.14-2.13.08-.4.08-.7 0-1.06-.15-.8-.57-1.6-1.14-2.13-.63-.64-1.42-1.05-2.35-1.05zM7.9 2c-.27.68-.42 1.3-.5 1.92-.11 1.18-.05 2.28.15 3.28-.5.29-1.07.44-1.7.44-.8 0-1.6-.23-2.25-.7C2.34 6.11 2.04 4.9 2.04 3.64c0-1.14.3-2.2.86-3.02C3.46.22 4.13 0 4.87 0c.5 0 1 .1 1.5.28C6.9.55 7.42.98 7.9 2z"/>
                <path d="M22.76 15.67c-.5.44-1.1.72-1.78.85-.46.09-.92.09-1.36 0-.7-.13-1.45-.5-2-.96-.66-.57-1.14-1.38-1.33-2.18-.15-.63-.15-1.28 0-1.92.2-.8.67-1.6 1.34-2.17.57-.47 1.16-.79 1.86-.92.49-.09.98-.09 1.49 0 .67.13 1.28.45 1.78.89.44.4.89.94 1.16 1.5.21.44.35.92.35 1.43 0 .51-.14.98-.35 1.42-.27.58-.72 1.14-1.16 1.53v.01zM10.72 21.34c-.7.49-1.43.8-2.21.92-.52.09-1.05.09-1.58 0-.78-.13-1.51-.43-2.17-.92-1.16-.85-1.9-2.05-1.9-3.29 0-.84.22-1.62.65-2.28.27-.42.6-.81.94-1.09.36-.28.71-.5 1.09-.65.57-.22 1.18-.35 1.82-.35.62 0 1.22.13 1.79.35.36.15.72.37 1.06.65.35.28.67.67.93 1.09.44.66.65 1.44.65 2.28 0 1.24-.67 2.44-1.86 3.29h.01z"/>
              </svg>
              Apple Calendar (.ics)
            </a>
          </li>
          </ul>
        </div>
      )}
      
      <div className="hidden md:block">            
            <VignetteTournoi />            
      </div>
      <div className="card-body">
        <div className="flex md:hidden">
          <div
            className="justify-start h-30 w-20 bg-cover bg-center transition-all duration-300"
            style={{ backgroundImage: `url(${imgTournoi9})` }} 
          />
          <div className="justify-center ml-4">
            <h2 className="text-lg font-semibold">{tournoi.tournoiLib}</h2>
            <div className="flex items-center gap-2">
        
            <img 
                    src={iconeCalendar} 
                    alt="Calendar Icon" 
                    className="w-5 h-5 object-contain"
                />
            {tournoi.tournoiDateDebut === tournoi.tournoiDateFin
                            ? new Date(tournoi.tournoiDateDebut).toLocaleDateString('fr-FR')
            : `du ${new Date(tournoi.tournoiDateDebut).toLocaleDateString('fr-FR')} au ${new Date(tournoi.tournoiDateFin).toLocaleDateString('fr-FR')}`} 
            
            </div>
            <div className="flex items-center gap-2">
        
              <img 
                    src={iconeTennis} 
                    alt="Calendar Icon" 
                    className="w-5 h-5 object-contain"
                />        
                <span className="font-semibold">Ligue:</span> {tournoi.ligue}        
            </div>
            <div>
              <p>
                <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M4 3a1 1 0 000 2h12a1 1 0 100-2H4zM3 7a1 1 0 011-1h12a1 1 0 011 1v1H3V7zm0 4h14v1a1 1 0 01-1 1H4a1 1 0 01-1-1v-1zm0 4h14v1a1 1 0 01-1 1H4a1 1 0 01-1-1v-1z" />
                </svg>
                <span className="font-semibold">Catégorie:</span> {tournoi.categorie}</p>
              <p>
                <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a7 7 0 100 14 7 7 0 000-14zm0 2a5 5 0 110 10A5 5 0 019 4zm0 2a3 3 0 100 6 3 3 0 000-6z" />
                </svg>
                <span className="font-semibold">Épreuve:</span> {tournoi.epreuve}</p>
              </div>
              <div className="btn btn-md bg-orange-300 hover:bg-orange-400 text-right">
                <button
                onClick={() => setShowClubDetails(true)}
                >
                Infos Club
                </button>
            </div>
          </div>                    
        </div>

        <div className="hidden md:block ">
          <div>
          <h2 className="text-base font-semibold mb-2">{tournoi.tournoiLib}</h2>
          </div>
          <div className="flex items-center gap-2">
          
              <img 
                  src={iconeCalendar} 
                  alt="Calendar Icon" 
                  className="w-5 h-5 object-contain"
              />
          {tournoi.tournoiDateDebut === tournoi.tournoiDateFin
                          ? new Date(tournoi.tournoiDateDebut).toLocaleDateString('fr-FR')
          : `du ${new Date(tournoi.tournoiDateDebut).toLocaleDateString('fr-FR')} au ${new Date(tournoi.tournoiDateFin).toLocaleDateString('fr-FR')}`} 
          
          </div>
            <div className="flex items-center gap-2">
            
                <img 
                    src={iconeTennis} 
                    alt="Calendar Icon" 
                    className="w-5 h-5 object-contain"
                />        
                <span className="font-semibold">Ligue:</span> {tournoi.ligue}        
            </div>

            <div>
              <p>
                  <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 3a1 1 0 000 2h12a1 1 0 100-2H4zM3 7a1 1 0 011-1h12a1 1 0 011 1v1H3V7zm0 4h14v1a1 1 0 01-1 1H4a1 1 0 01-1-1v-1zm0 4h14v1a1 1 0 01-1 1H4a1 1 0 01-1-1v-1z" />
                  </svg>
                  <span className="font-semibold">Catégorie:</span> {tournoi.categorie}</p>
              <p>
                <svg className="w-5 h-5 inline-block mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a7 7 0 100 14 7 7 0 000-14zm0 2a5 5 0 110 10A5 5 0 019 4zm0 2a3 3 0 100 6 3 3 0 000-6z" />
                </svg>
                <span className="font-semibold">Épreuve:</span> {tournoi.epreuve}</p>
            </div>
          <div className="card-actions justify-center mt-5">
            <button
              className="btn btn-md bg-orange-300 hover:bg-orange-400"
              onClick={() => setShowClubDetails(true)}
            >
              Infos Club
            </button>
        </div>
        <div className="text-xs text-gray-400 mt-2 italic">Source : <a href="https://tournois.padelmagazine.fr/" target="_blank" rel="noopener" className="text-blue-400 hover:text-blue-300 underline">PadelMagazine</a>, récupéré le {new Date(tournoi.dateAjout).toLocaleDateString('fr-FR')} à {new Date(tournoi.dateAjout).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
      </div>

      {showClubDetails && club && (

        

        <div className="modal modal-open">
          <div className="modal-box">
            <div className="flex ">
              <h3 className=" font-bold text-2xl text-wrap">{club.clubLib}</h3>
            </div>
            {club.infosInscription?.length > 3 && (
              <>
                <hr className="h-px bg-gray-200 border-0 dark:bg-gray-700 my-3"></hr>
                <h3 className="font-bold text-xl">Infos Inscription</h3>
                <p className="py-1">{club.infosInscription}</p>
              </>
            )}
            <hr className="h-px bg-gray-200 border-0 dark:bg-gray-700 my-3"></hr>
            <h3 className="font-bold text-xl">Infos Club</h3>
            <p className="py-1"><span className="font-semibold">Adresse:</span> {club.clubAdresse}</p>
            <p  className="py-1"><span className="font-semibold">Téléphone:</span> {club.clubTel}</p>
            <p  className="py-1"><span className="font-semibold">Email:</span> <a href={`mailto:${club.clubMail}`} className="text-blue-500 underline">{club.clubMail}</a></p>
            
            
            <div className="modal-action">
              <button
                className="btn bg-yellow-600 hover:bg-yellow-500"
                onClick={() => setShowClubDetails(false)}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournoiCard;